/**
 * MoSale Configurator
 * MoSale Endpoint for Configurator Authentication
 */

import * as url from "url";
import * as portableFetch from "portable-fetch";
import { Configuration } from "./configuration";
import {ApiErrorType} from "App/constants";

const BASE_PATH = "http://127.0.0.1/fleet/rest/".replace(/\/+$/, "");

/**
 *
 * @export
 */
export const COLLECTION_FORMATS = {
    csv: ",",
    ssv: " ",
    tsv: "\t",
    pipes: "|",
};

/**
 *
 * @export
 * @interface FetchAPI
 */
export interface FetchAPI {
    (url: string, init?: any): Promise<Response>;
}

/**
 *
 * @export
 * @interface FetchArgs
 */
export interface FetchArgs {
    url: string;
    options: any;
}

/**
 *
 * @export
 * @class BaseAPI
 */
export class BaseAPI {
    protected configuration: Configuration;

    constructor(configuration?: Configuration, protected basePath: string = BASE_PATH, protected fetch: FetchAPI = portableFetch) {
        if (configuration) {
            this.configuration = configuration;
            this.basePath = configuration.basePath || this.basePath;
        }
    }
}

/**
 *
 * @export
 * @class RequiredError
 * @extends {Error}
 */
export class RequiredError extends Error {
    name: "RequiredError"
    constructor(public field: string, msg?: string) {
        super(msg);
    }
}


export interface Credentials {
    appversion: number
    username: string
    pwd: string
}

export interface AccountSelection {
    appversion: number
    authtenticationtoken: string
    accountId: string
}

export interface ResetPassword {
    appversion: number
    username: string
}

export interface Authentication {
    accounts: {
        [key: string]: {
            identity: {
                birthday?: string,
                firstname: string,
                fixphone?: string,
                function?: string,
                language?: string,
                lastname: string,
                mail?: string,
                phone?: string,
            },
            identitytype: string,
            role: string
        }
    }
    authtenticationtoken: string;
}

export interface AccountInfo {
    authtenticationtoken: string;
}

export interface Payload {
    authentication?: Authentication
    account?: AccountInfo
}

export interface ErrorPayload {
    code?: number
    message?: string
}

export interface AuthError {
    errorcode: string
}

export interface AuthResponse {
    retcode: number
    payload: Payload
    error?: ErrorPayload
}

export interface SelectAccountResponse {
    retcode: number
    payload: Payload
    error?: ErrorPayload
}

export interface ResetPasswordResponse {
    retcode: number
    payload?: Payload
    error?: ErrorPayload
}

/**
 * DefaultApi - fetch parameter creator
 * @export
 */
export const DefaultApiFetchParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * @summary performs a login. Returns a list of identities
         * @param {Credentials} [currentConfiguration] the credentials for the login
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        submitLogin(credentials: Credentials, options: any = {}): FetchArgs {
            const localVarPath = `/session/authentication`;
            const localVarUrlObj = url.parse(localVarPath, true);
            const localVarRequestOptions = Object.assign({ method: 'PUT' }, options);
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {...credentials} as any;

            localVarUrlObj.query = Object.assign({}, localVarUrlObj.query, localVarQueryParameter, options.query);
            // fix override query string Detail: https://stackoverflow.com/a/7517673/1077943
            delete localVarUrlObj.search;
            localVarRequestOptions.headers = Object.assign({}, localVarHeaderParameter, options.headers);
            localVarRequestOptions.body =  undefined;

            return {
                url: url.format(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },

        /**
         * @summary performs the selection of an account.
         * @param {AccountSelection} [currentConfiguration] the account selection
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        selectAccount(accountSelection: AccountSelection, options: any = {}): FetchArgs {
            const localVarPath = '/session/authentication/' + accountSelection.authtenticationtoken + '/account/' + accountSelection.accountId;
            const localVarUrlObj = url.parse(localVarPath, true);
            const localVarRequestOptions = Object.assign({ method: 'PUT' }, options);
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {appversion:accountSelection.appversion} as any;

            localVarUrlObj.query = Object.assign({}, localVarUrlObj.query, localVarQueryParameter, options.query);
            // fix override query string Detail: https://stackoverflow.com/a/7517673/1077943
            delete localVarUrlObj.search;
            localVarRequestOptions.headers = Object.assign({}, localVarHeaderParameter, options.headers);
            localVarRequestOptions.body =  undefined;

            return {
                url: url.format(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },

        /**
         * @summary request a reset password
         * @param {ResetPassword} the credentials for the login
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        resetPassword(resetpassword: ResetPassword, options: any = {}): FetchArgs {
            const localVarPath = `/session/password`;
            const localVarUrlObj = url.parse(localVarPath, true);
            const localVarRequestOptions = Object.assign({ method: 'POST' }, options);
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {appversion:resetpassword.appversion} as any;

            localVarUrlObj.query = Object.assign({}, localVarUrlObj.query, localVarQueryParameter, options.query);
            // fix override query string Detail: https://stackoverflow.com/a/7517673/1077943
            delete localVarUrlObj.search;
            localVarRequestOptions.headers = Object.assign({}, localVarHeaderParameter, options.headers);
            localVarRequestOptions.body =  JSON.stringify({username:resetpassword.username});

            return {
                url: url.format(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * DefaultApi - functional programming interface
 * @export
 */
export const DefaultApiFp = function(configuration?: Configuration) {
    return {
        submitLogin(credentials: Credentials, options?: any): (fetch?: FetchAPI, basePath?: string) => Promise<AuthResponse | AuthError> {
            const localVarFetchArgs = DefaultApiFetchParamCreator(configuration).submitLogin(credentials, options);
            return (fetch: FetchAPI = portableFetch, basePath: string = BASE_PATH) => {
                return fetch(basePath + localVarFetchArgs.url, localVarFetchArgs.options).then((response) => {
                    if (response.status >= 200 && response.status < 300) {
                        return response.json();
                    } else {
                        return { errorcode: ApiErrorType.INVALID_CREDENTIALS};
                    }
                }).catch((reason) => {
                    return { errorcode: ApiErrorType.SERVER_UNAVAILABLE};
                });
            };
        },

        selectAccount(accountSelection: AccountSelection, options?: any): (fetch?: FetchAPI, basePath?: string) => Promise<SelectAccountResponse | AuthError> {
            const localVarFetchArgs = DefaultApiFetchParamCreator(configuration).selectAccount(accountSelection, options);
            return (fetch: FetchAPI = portableFetch, basePath: string = BASE_PATH) => {
                return fetch(basePath + localVarFetchArgs.url, localVarFetchArgs.options).then((response) => {
                    if (response.status >= 200 && response.status < 300) {
                        return response.json();
                    } else {
                        return { errorcode: ApiErrorType.INVALID_CREDENTIALS};
                    }
                }).catch((reason) => {
                    return { errorcode: ApiErrorType.SERVER_UNAVAILABLE};
                });
            };
        },

        resetPassword(resetpassword: ResetPassword, options?: any): (fetch?: FetchAPI, basePath?: string) => Promise<ResetPasswordResponse | AuthError> {
            const localVarFetchArgs = DefaultApiFetchParamCreator(configuration).resetPassword(resetpassword, options);
            return (fetch: FetchAPI = portableFetch, basePath: string = BASE_PATH) => {
                return fetch(basePath + localVarFetchArgs.url, localVarFetchArgs.options).then((response) => {
                    if (response.status >= 200 && response.status < 300) {
                        return response.json();
                    } else {
                        return { errorcode: ApiErrorType.SERVER_UNAVAILABLE};
                    }
                }).catch((reason) => {
                    return { errorcode: ApiErrorType.SERVER_UNAVAILABLE};
                });
            };
        },

    }
};

/**
 * DefaultApi - factory interface
 * @export
 */
export const DefaultApiFactory = function (configuration?: Configuration, fetch?: FetchAPI, basePath?: string) {

    return {
        submitLogin(credentials: Credentials, options?: any) {
            return DefaultApiFp(configuration).submitLogin(credentials, options)(fetch, basePath);
        },

        selectAccount(accountSelection: AccountSelection, options?: any) {
            return DefaultApiFp(configuration).selectAccount(accountSelection, options)(fetch, basePath);
        },

        resetPassword(resetpassword: ResetPassword, options?: any) {
            return DefaultApiFp(configuration).resetPassword(resetpassword, options)(fetch, basePath);
        }
    };
};

/**
 * DefaultApi - object-oriented interface
 * @export
 * @class DefaultApi
 * @extends {BaseAPI}
 */
export class DefaultApi extends BaseAPI {

    public submitLogin(credentials: Credentials, options?: any) {
        return DefaultApiFp(this.configuration).submitLogin(credentials, options)(this.fetch, this.basePath);
    }

    public selectAccount(accountSelection: AccountSelection, options?: any) {
        return DefaultApiFp(this.configuration).selectAccount(accountSelection, options)(this.fetch, this.basePath);
    }

    public resetPassword(resetpassword: ResetPassword, options?: any) {
        return DefaultApiFp(this.configuration).resetPassword(resetpassword, options)(this.fetch, this.basePath);
    }
}

/*
{
  "error": null,
  "payload": {
    "authentication": {
      "accounts": {
        "297a03c7-4e02-425d-bc83-c20b1d89c72d": {
          "identity": {
            "address": {},
            "birthday": "1962-04-01",
            "firstname": "Mirko",
            "fixphone": "+41619359393",
            "function": "Inhaber",
            "id": {
              "customernumber": "08380",
              "id": "12",
              "type": "01"
            },
            "language": "D",
            "lastname": "Tozzo",
            "mail": "mirko.tozzo@tozzo.ch",
            "phone": "+41796892385",
            "title": 1
          },
          "identitytype": "influencer",
          "role": "customer-admin"
        }
      },
      "authtenticationtoken": "357e60fd-ecb8-4a19-ac54-dad0ec64d9cb",
      "identities": {
        "customer": [
          {
            "address": {
              "city": "Bubendorf",
              "country": "CH",
              "name": "Grüngenstrasse 39",
              "zip": "4416"
            },
            "customernumber": "08380",
            "faxnumber": "+41619359398",
            "id": "305fb668-3d3b-4e6c-aef6-ed4aa7043e06",
            "industricodes": {
              "200": "Schwere Erdb./Strabau (Local C)",
              "255": "Gebäuderückbau",
              "322": "Papierindustrie"
            },
            "isholding": true,
            "language": "D",
            "maindivision": "B",
            "mainsegment": "01",
            "name": "Tozzo AG BL",
            "nps": {
              "lastyear": 8,
              "runningyear": 8
            },
            "phonenumber": "+41619359393",
            "turnover3years": 802250
          }
        ],
        "employee": [],
        "influencer": [
          {
            "address": {},
            "birthday": "1962-04-01",
            "firstname": "Mirko",
            "fixphone": "+41619359393",
            "function": "Inhaber",
            "id": {
              "customernumber": "08380",
              "id": "12",
              "type": "01"
            },
            "language": "D",
            "lastname": "Tozzo",
            "mail": "mirko.tozzo@tozzo.ch",
            "phone": "+41796892385",
            "title": 1
          }
        ]
      },
      "isroot": true,
      "username": "root@@mirko.tozzo@tozzo.ch"
    }
  },
  "retcode": 0
}
 */


// WEBPACK FOOTER //
// ./src/services/auth.ts